import { memo, Suspense, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DocumentationPageType } from './types';
import {
  setActivePageTab,
  selectDocumentationPageType,
  setDocumentationPageType,
  selectActiveDocumentationPageTab,
  DEFAULT_OBJECTS_TAB_ID,
} from '../../reducers/documentation/documentationPageReducer';
import { CenteredCircularProgress } from '../common/CenteredCircularProgress';
import { useExpandedMode } from '../pages/configuration-canvas/panels-reserve-space/useExpandedMode';
import {
  clearDocumentationDialog,
  selectSingleObject,
} from '../../reducers/documentation/documentationReducer';
import { DocumentationTabs } from './tabs/DocumentationTabs';
import { DocumentationPageHeader } from './DocumentationPageHeader';
import Stack from '@mui/material/Stack';
import useMediaQuery from '@mui/material/useMediaQuery';
import { UniversalSearchProvider } from './universal-search/UniversalSearchContext';
import { MetadataTypeWrapper } from './MetadataTypeWrapper';
import { TagsAndUsersWrapper } from './TagsAndUsersWrapper';
import { BaseDependenciesLogic } from './building-blocks/content/BaseDependenciesLogic';
import { UniversalSearch } from './universal-search/UniversalSearch';
import { loadTabsForOrg } from '../../reducers/documentation/documentationPageReducer';
import { DocumentationFiltersProvider } from './selected-object/filters/DocumentationFiltersContext';
import { useHandleDeepLinkParams } from './useHandleDocumentationDeepLinkParams';
import { useInitializeMetadataTypes } from './hooks/useInitializeMetadataTypes';

interface DocumentationDialogProps {
  crmOrgId: string;
  onCloseDialog?: () => void;
  hideAddObjectButton?: boolean;
  hasFunnelMap?: boolean;
}

const DocumentationDialog = memo(
  ({
    crmOrgId,
    hasFunnelMap,
    onCloseDialog,
    hideAddObjectButton,
  }: DocumentationDialogProps & { hideAddObjectButton?: boolean }) => {
    const { isExpandedMode } = useExpandedMode();
    const dispatch = useDispatch();

    const documentationPageType = useSelector(selectDocumentationPageType);
    const activePageTab = useSelector(selectActiveDocumentationPageTab);
    const activeObjectName = useSelector(selectSingleObject);

    const isSmallScreen = useMediaQuery('(max-width: 1200px)');
    const isUniversalSearch = documentationPageType === DocumentationPageType.universalSearch;

    useHandleDeepLinkParams({
      isExpandedMode,
      documentationPageType,
      activePageTab,
      activeObjectName,
    });

    useEffect(() => {
      dispatch(loadTabsForOrg({ crmOrgId }));
    }, [dispatch, crmOrgId]);

    useInitializeMetadataTypes();

    useEffect(() => {
      return () => {
        dispatch(setActivePageTab({ tabId: DEFAULT_OBJECTS_TAB_ID }));
        dispatch(
          setDocumentationPageType({
            documentationPageType: DocumentationPageType.metadataTypes,
          }),
        );
      };
    }, [dispatch]);

    useEffect(() => {
      return () => {
        dispatch(clearDocumentationDialog());
      };
    }, [dispatch, crmOrgId]);

    const renderNewDocumentationContent = () => {
      if (documentationPageType === DocumentationPageType.metadataTypes) {
        return (
          <MetadataTypeWrapper
            onCloseDialog={onCloseDialog}
            crmOrgId={crmOrgId}
            hideAddObjectButton={hideAddObjectButton}
            isExpandedMode={isExpandedMode}
          />
        );
      }

      if (documentationPageType === DocumentationPageType.tagsAndUsers) {
        return <TagsAndUsersWrapper crmOrgId={crmOrgId} />;
      }
    };

    return (
      <DocumentationFiltersProvider>
        <Suspense fallback={<CenteredCircularProgress />}>
          <UniversalSearchProvider>
            <Stack
              height={hasFunnelMap ? 'calc(100% - 56px)' : '100%'} //56px is the height of the env selector and gap
              width="100%"
              flexDirection="row"
              flexWrap="nowrap"
            >
              <Stack
                height="100%"
                gap={isExpandedMode ? 3 : 2}
                flex={1}
                sx={{
                  overflow: 'hidden',
                  minWidth: 0,
                  width: isSmallScreen ? '100%' : 'auto',
                }}
              >
                <DocumentationPageHeader
                  withExpandedModeButton
                  renderBreadcrumbs={!isExpandedMode}
                  onCloseDialog={onCloseDialog}
                  crmOrgId={crmOrgId}
                />

                {!isUniversalSearch && (
                  <DocumentationTabs
                    documentationPageType={documentationPageType}
                    crmOrgId={crmOrgId}
                  >
                    {renderNewDocumentationContent()}
                  </DocumentationTabs>
                )}

                {isUniversalSearch && (
                  <UniversalSearch crmOrgId={crmOrgId} isExpandedMode={isExpandedMode} />
                )}
              </Stack>

              <BaseDependenciesLogic
                crmOrgId={crmOrgId}
                onCloseDocumentationDialog={onCloseDialog}
              />
            </Stack>
          </UniversalSearchProvider>
        </Suspense>
      </DocumentationFiltersProvider>
    );
  },
);

export default DocumentationDialog;
